/**
 * Oeffentliche Registrierung — erstellt eigenen Workspace (Firma) + Benutzerkonto,
 * strikt mandantengetrennt. Nach Erfolg direkt eingeloggt (Session-Cookie).
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, User, Building2, Mail, Lock, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDetail(detail) {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return "Registrierung fehlgeschlagen. Bitte erneut versuchen.";
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const [f, setF] = useState({ name: "", company_name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/register`, f, { withCredentials: true });
      window.location.href = "/";
    } catch (ex) {
      setErr(formatDetail(ex.response?.data?.detail));
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <img src="/invictaflow-logo.png" alt="InvictaFlow" className="w-16 h-16 mx-auto mb-3 rounded-xl shadow-sm" />
          <h1 className="text-2xl font-bold font-heading">Konto erstellen</h1>
          <p className="text-sm text-slate-500 mt-1">Eigener Workspace für dein Unternehmen oder deine Agentur — kostenlos starten.</p>
        </div>
        <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4" data-testid="register-form">
          <div className="relative">
            <User className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input required value={f.name} onChange={set("name")} placeholder="Dein Name"
              className="pl-10 h-11" data-testid="register-name" />
          </div>
          <div className="relative">
            <Building2 className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input required value={f.company_name} onChange={set("company_name")} placeholder="Firmen- / Workspace-Name"
              className="pl-10 h-11" data-testid="register-company" />
          </div>
          <div className="relative">
            <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input required type="email" value={f.email} onChange={set("email")} placeholder="E-Mail-Adresse"
              className="pl-10 h-11" data-testid="register-email" />
          </div>
          <div className="relative">
            <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input required type="password" minLength={8} value={f.password} onChange={set("password")}
              placeholder="Passwort (mind. 8 Zeichen)" className="pl-10 h-11" data-testid="register-password" />
          </div>
          {err && <p className="text-red-500 text-xs text-center" data-testid="register-error">{err}</p>}
          <Button type="submit" disabled={submitting} className="w-full h-11 gap-1.5" data-testid="register-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Workspace erstellen <ArrowRight className="w-4 h-4" />
          </Button>
          <p className="text-[11px] text-slate-400 text-center">
            Mit der Registrierung akzeptierst du unsere{" "}
            <Link to="/terms" className="underline">Nutzungsbedingungen</Link> und{" "}
            <Link to="/privacy" className="underline">Datenschutzerklärung</Link>.
          </p>
        </form>
        <p className="text-center text-sm text-slate-500 mt-4">
          Bereits ein Konto? <Link to="/login" className="text-[#2563EB] underline" data-testid="register-login-link">Anmelden</Link>
        </p>
      </div>
    </div>
  );
}
